import React, { useEffect } from 'react';
import { Trophy, Clock, Target, TrendingUp, RotateCcw } from 'lucide-react';
import confetti from 'canvas-confetti';

/**
 * Session summary with stats and encouraging feedback
 */
export default function SessionSummary({ sessionData, phaseDurations, onRestart }) {
  const totalDurationMinutes = (sessionData?.totalDuration / 60)?.toFixed(1);
  const avgInhaleExhaleRatio = (phaseDurations?.inhale / phaseDurations?.exhale)?.toFixed(2);

  // Calculate consistency score (simple metric based on pause count)
  const consistencyScore = Math.max(0, 100 - (sessionData?.pauseCount * 10));

  // Get encouraging message based on duration
  const getEncouragingMessage = () => {
    const minutes = parseFloat(totalDurationMinutes);
    
    if (minutes < 2) {
      return {
        emoji: '🌱',
        title: 'Great Start!',
        message: 'Good start—try a 3-minute streak next!',
        color: 'from-green-400 to-emerald-500'
      };
    } else if (minutes >= 2 && minutes < 5) {
      return {
        emoji: '🎯',
        title: 'Nice Control!',
        message: 'Nice control—add it before your song run!',
        color: 'from-blue-400 to-cyan-500'
      };
    } else {
      return {
        emoji: '⭐',
        title: 'Pro-Level!',
        message: 'Pro-level breath focus—great work.',
        color: 'from-purple-400 to-pink-500'
      };
    }
  };

  const encouragement = getEncouragingMessage();

  // Trigger confetti on mount
  useEffect(() => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#10b981', '#3b82f6', '#8b5cf6', '#ec4899']
    });
  }, []);

  return (
    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-block bg-gradient-to-r from-yellow-400 to-orange-500 p-4 rounded-full mb-4">
          <Trophy className="w-12 h-12 text-white" />
        </div>
        <h2 className="text-3xl font-bold text-white mb-2">
          🎬 Curtain Call Summary
        </h2>
        <p className="text-white/80">Session complete!</p>
      </div>
      {/* Stats Grid */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* Total Time */}
        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <Clock className="w-6 h-6 text-blue-400" />
            <h3 className="text-white/80 font-medium">Total Time Practiced</h3>
          </div>
          <div className="text-4xl font-bold text-white">
            {totalDurationMinutes} <span className="text-2xl text-white/60">min</span>
          </div>
        </div>

        {/* Cycles Completed */}
        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <Target className="w-6 h-6 text-green-400" />
            <h3 className="text-white/80 font-medium">Cycles Completed</h3>
          </div>
          <div className="text-4xl font-bold text-white">
            {sessionData?.completedCycles} <span className="text-2xl text-white/60">cycles</span>
          </div>
        </div>

        {/* Inhale/Exhale Ratio */}
        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <TrendingUp className="w-6 h-6 text-purple-400" />
            <h3 className="text-white/80 font-medium">Avg Inhale/Exhale Ratio</h3>
          </div>
          <div className="text-4xl font-bold text-white">
            {avgInhaleExhaleRatio} <span className="text-2xl text-white/60">:1</span>
          </div>
        </div>

        {/* Consistency Score */}
        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <Trophy className="w-6 h-6 text-yellow-400" />
            <h3 className="text-white/80 font-medium">Consistency Score</h3>
          </div>
          <div className="text-4xl font-bold text-white">
            {consistencyScore} <span className="text-2xl text-white/60">/ 100</span>
          </div>
          <p className="text-white/60 text-sm mt-2">
            {sessionData?.pauseCount === 0 ? 'Perfect flow!' : `${sessionData?.pauseCount} pause${sessionData?.pauseCount > 1 ? 's' : ''}`}
          </p>
        </div>
      </div>
      {/* Encouraging Message */}
      <div className={`bg-gradient-to-r ${encouragement?.color} rounded-xl p-6 mb-8 text-center`}>
        <div className="text-5xl mb-3">{encouragement?.emoji}</div>
        <h3 className="text-2xl font-bold text-white mb-2">{encouragement?.title}</h3>
        <p className="text-white/90 text-lg">{encouragement?.message}</p>
      </div>
      {/* Pattern Summary */}
      <div className="bg-white/5 rounded-xl p-6 border border-white/10 mb-8">
        <h3 className="text-white font-medium mb-4">Your Pattern</h3>
        <div className="flex justify-around text-center">
          <div>
            <div className="text-blue-400 text-sm mb-1">Inhale</div>
            <div className="text-2xl font-bold text-white">{phaseDurations?.inhale}s</div>
          </div>
          <div>
            <div className="text-teal-400 text-sm mb-1">Hold In</div>
            <div className="text-2xl font-bold text-white">{phaseDurations?.hold_in}s</div>
          </div>
          <div>
            <div className="text-pink-400 text-sm mb-1">Exhale</div>
            <div className="text-2xl font-bold text-white">{phaseDurations?.exhale}s</div>
          </div>
          <div>
            <div className="text-purple-400 text-sm mb-1">Hold Out</div>
            <div className="text-2xl font-bold text-white">{phaseDurations?.hold_out}s</div>
          </div>
        </div>
      </div>
      {/* Action Button */}
      <div className="text-center">
        <button
          onClick={onRestart}
          className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg transition-all transform hover:scale-105"
        >
          <RotateCcw className="w-6 h-6" />
          Start New Session
        </button>
      </div>
    </div>
  );
}